'use client'

import { useRouter } from 'next/navigation'
import type { Route } from 'next'
import Image from 'next/image'
import { ArrowRight } from 'lucide-react'
import { DecorativeBackground } from '@/components/brand/decorativeBackground'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { SERVICES } from '@/config/services'
import { TrackMyStatusDialog } from '@/features/public'

export function ChooseServiceView() {
  const router = useRouter()

  return (
    <div className="relative flex min-h-svh flex-col overflow-hidden font-[family-name:var(--font-display)]">
      <DecorativeBackground src="/img/checkup-bg.webp" />
      <div className="bg-background/75 pointer-events-none fixed inset-0 z-0" aria-hidden="true" />

      <main className="relative z-10 mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center px-4 py-8 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <Badge variant="secondary">Choose your service</Badge>
          <h1 className="mt-2 text-xl font-bold tracking-tight md:text-2xl">
            How would you like to start?
          </h1>
          <p className="text-muted-foreground mt-1.5 text-sm leading-snug">
            Upload your portfolio, get it reviewed by a financial expert, and receive an audit built
            around your goals.
          </p>
          <div className="mt-2 flex justify-center">
            <TrackMyStatusDialog />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:mt-5 md:grid-cols-3">
          {SERVICES.map((service) => (
            <Card
              key={service.key}
              size="sm"
              className="shadow-xs justify-between transition duration-300 ease-out hover:-translate-y-1.5 hover:shadow-lg motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="px-(--card-spacing)">
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg border">
                  <Image
                    src={service.image}
                    alt=""
                    fill
                    sizes="(min-width: 768px) 33vw, 100vw"
                    className="object-cover transition-transform duration-500 ease-out group-hover/card:scale-[1.05] motion-reduce:transition-none motion-reduce:group-hover/card:scale-100"
                  />
                </div>
              </div>

              <CardHeader>
                <Badge variant="secondary" className="mb-0.5 w-fit">
                  {service.badge}
                </Badge>
                <CardTitle className="text-base font-bold">{service.title}</CardTitle>
                <CardDescription className="text-xs leading-snug">
                  {service.description}
                </CardDescription>
              </CardHeader>

              <CardFooter className="border-t-0 bg-transparent">
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => router.push(service.path as Route)}
                >
                  {service.action}
                  <ArrowRight className="size-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </main>
    </div>
  )
}
